import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { finalize } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpErrorResponse } from '@angular/common/http';

import { DialogService } from 'src/app/services/core/dialog.service';
import { AdminTTDDialogService } from 'src/app/services/dialogs/admin-ttd-dialog.service';
import { AdminTTDService } from 'src/app/services/admin/admin-ttd.service';
import { ThemeOptions } from 'src/app/theme-options';
import { BasePageComponent } from 'src/app/components/core/base-page/base-page.component';

import {
  TtdPartner,
  TtdAdvertiser,
  TtdPartnerCreateDto,
  TtdPartnerUpdateDto
} from '../../../models/services/admin/ttd';
import { UsersService } from 'src/app/services/users.service';
import { Router } from '@angular/router';

type SortKey = 'id' | 'name' | 'secret' | 'advertisers';

@Component({
  selector: 'app-admin-ttd',
  templateUrl: './admin-ttd.component.html',
  styleUrls: ['./admin-ttd.component.sass']
})
export class AdminTTDComponent extends BasePageComponent implements OnInit, OnDestroy {

  heading = 'TTD Manage';
  subheading = '';
  icon = 'pe-7s-user icon-gradient bg-midnight-bloom';

  search = '';
  expanded = new Set<string>();

  sortActive: SortKey = 'name';
  sortDir: 'asc' | 'desc' = 'asc';
  pageIndex = 0;
  pageSize = 5;
  pageSizeOptions: number[] = [5, 10, 25, 50];

  loading = false;
  isDirty = false;
  private baseline: TtdPartner[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  partners: TtdPartner[] = [];

  constructor(
    public dialogService: DialogService,
    public adminTTDDDialogService: AdminTTDDialogService,
    private ttdService: AdminTTDService,
    private globals: ThemeOptions,
    private spinnerService: NgxSpinnerService,
    private usersService: UsersService,
    private router: Router
  ) {
    super(usersService.hasGlobalActionRight('Admin', 'TtdManagement', 'Manage'),router)
  }

  ngOnInit(): void {
    if(this.redirecting)
      return;
    this.spinnerService.show();
    this.globals.adminMode = true;
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.ttdService.getTtdPartner()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (data: TtdPartner[]) => {
          this.partners = data ?? [];
          this.setBaseline();
          this.pageIndex = 0;
          this.autoExpandMatches();
          this.spinnerService.hide();
        },
        error: () => this.dialogService.showMessage('Error', 'Error loading partners')
      });
  }

  ngOnDestroy(): void {
    this.globals.adminMode = false;
  }

  private filterPredicate(row: TtdPartner, filter: string): boolean {
    const f = (filter || '').trim().toLowerCase();
    const base = `${row.name ?? ''} ${row.id ?? ''} ${row.firstPartyDataSecretKey ?? ''}`.toLowerCase();
    const inBase = base.includes(f);
    const inAdv = Array.isArray(row.advertisers) &&
                  row.advertisers.some(a => (`${a.name ?? ''} ${a.id ?? ''}`).toLowerCase().includes(f));
    return inBase || inAdv;
  }

  private hasAdvMatch(row: TtdPartner, q: string): boolean {
    const f = (q || '').trim().toLowerCase();
    if (!f) return false;
    return Array.isArray(row.advertisers) &&
           row.advertisers.some(a => (`${a.name ?? ''} ${a.id ?? ''}`).toLowerCase().includes(f));
  }

  private autoExpandMatches(): void {
    this.expanded.clear();
    const q = this.search?.trim();
    if (!q) return;
    for (const row of this.filtered) {
      if (this.hasAdvMatch(row, q)) this.expanded.add(row.id);
    }
  }

  applyFilter(value?: string) {
    if (typeof value === 'string') this.search = value;
    this.pageIndex = 0;
    this.autoExpandMatches();
  }

  advertisersCount(row: TtdPartner): number {
    return Array.isArray(row.advertisers) ? row.advertisers.length : 0;
  }

  toggleRow(row: TtdPartner) {
    if (this.advertisersCount(row) <= 0) return;
    if (this.expanded.has(row.id)) this.expanded.delete(row.id);
    else this.expanded.add(row.id);
  }

  // ——— CRUD ———
  createPartner() {
    const usedIds = this.getUsedIds();

    this.adminTTDDDialogService.createPartner(usedIds).then(res => {
      if (res.action !== 'createPartner') return;

      const dto: TtdPartnerCreateDto = res.data;

      const entity: TtdPartner = {
        id: dto.id.trim(),
        name: dto.name.trim(),
        firstPartyDataSecretKey: dto.firstPartyDataSecretKey?.trim(),
        advertisers: this.normalizeAds(dto.advertisers)
      };

      this.partners = [entity, ...this.partners];
      this.pageIndex = 0;
      this.autoExpandMatches();
      this.markDirty();

      this.dialogService.showMessage('Partners', 'Partner added locally. Click “Save” to persist');
    });
  }


  editPartner(row: TtdPartner) {
    const usedIds = this.getUsedIds();

    this.adminTTDDDialogService.editPartner(row, usedIds).then(res => {
      if (res.action !== 'updatePartner') return;

      const dto: TtdPartnerUpdateDto = res.data;
      const idx = this.partners.findIndex(p => p.id === res.id);
      if (idx === -1) return;

      const oldId = this.partners[idx].id;
      const newId = ('newId' in res && res.newId) ? res.newId.trim() : undefined;

      const updated: TtdPartner = {
        ...this.partners[idx],
        id: newId ?? this.partners[idx].id,
        name: dto.name?.trim() ?? this.partners[idx].name,
        firstPartyDataSecretKey: dto.firstPartyDataSecretKey?.trim() ?? this.partners[idx].firstPartyDataSecretKey,
        advertisers: this.normalizeAds(dto.advertisers ?? this.partners[idx].advertisers)
      };

      this.partners = [
        ...this.partners.slice(0, idx),
        updated,
        ...this.partners.slice(idx + 1)
      ];

      if (newId && this.expanded.has(oldId)) {
        this.expanded.delete(oldId);
        this.expanded.add(newId);
      }

      this.autoExpandMatches();
      this.markDirty();
      this.dialogService.showMessage('Partners', 'Partner edited locally. Click “Save” to persist');
    });
  }


  deletePartner(partner: TtdPartner) {
    this.adminTTDDDialogService.confirmDeletePartner(partner).then(res => {
      if (res.action !== 'deletePartner') return;

      this.expanded.delete(res.id);
      this.partners = this.partners.filter(p => p.id !== res.id);

      this.markDirty();

      this.dialogService.showMessage('Partners', 'Partner deleted locally. Click “Save” to persist');
    });
  }

  editAdvertiser(row: TtdPartner, a: TtdAdvertiser) {
    const usedAdvIds = new Set((row.advertisers ?? []).map(x => x.id));

    this.adminTTDDDialogService
      .editAdvertiser(row, a, usedAdvIds)
      .then(res => {
        if (res.action !== 'updateAdvertiser') return;

        const pIdx = this.partners.findIndex(p => p.id === res.partnerId);
        if (pIdx === -1) return;

        const ads = [...(this.partners[pIdx].advertisers ?? [])];
        const aIdx = ads.findIndex(x => x.id === res.id);
        if (aIdx === -1) return;

        const newAdvId = ('newId' in res && res.newId) ? res.newId.trim() : undefined;

        ads[aIdx] = { ...ads[aIdx], id: newAdvId ?? ads[aIdx].id,  name: res.data.name.trim() };

        this.partners = [
          ...this.partners.slice(0, pIdx),
          { ...this.partners[pIdx], advertisers: ads },
          ...this.partners.slice(pIdx + 1)
        ];

        this.autoExpandMatches();
        this.markDirty();
        this.dialogService.showMessage('Advertiser', 'Edited locally. Click “Save” to persist');
      });
  }

  deleteAdvertiser(row: TtdPartner, a: TtdAdvertiser) {
    this.adminTTDDDialogService
      .confirmDeleteAdvertiser(row, a)
      .then(res => {
        if (res.action !== 'deleteAdvertiser') return;

        const pIdx = this.partners.findIndex(p => p.id === res.partnerId);
        if (pIdx === -1) return;

        const nextAds = (this.partners[pIdx].advertisers ?? []).filter(x => x.id !== res.id);

        this.partners = [
          ...this.partners.slice(0, pIdx),
          { ...this.partners[pIdx], advertisers: nextAds },
          ...this.partners.slice(pIdx + 1)
        ];

        if (nextAds.length === 0) this.expanded.delete(res.partnerId);
        this.autoExpandMatches();
        this.markDirty();
        this.dialogService.showMessage('Advertiser', 'Deleted locally. Click “Save” to persist');
      });
  }


  savePartners(): void {
    this.updatePartners(this.partners, 'Changes saved successfully');
  }

  private updatePartners(partners: TtdPartner[], showMessage: string) {
    this.spinnerService.show();
    const prevExpanded  = new Set(this.expanded ?? []);
    const prevPageIndex = this.pageIndex ?? 0;
    const prevPageSize  = this.pageSize  ?? 10;
    const prevSearch    = this.search    ?? '';

    this.loading = true;

    this.ttdService.updateTtdPartners(partners)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.partners = [...partners];
          this.search = prevSearch;
          if (typeof this.applyFilter === 'function') {
            this.applyFilter();
          }

          if (typeof this.changePageSize === 'function') {
            this.changePageSize(prevPageSize);
          }
          if (typeof this.goToPage === 'function' && typeof this.totalPages !== 'undefined') {
            const safeIndex = Math.max(0, Math.min(prevPageIndex, this.totalPages - 1));
            this.goToPage(safeIndex);
          }
          const idsVigentes = new Set(this.partners.map(p => p.id));
          this.expanded = new Set([...prevExpanded].filter(id => idsVigentes.has(id)));
          this.dialogService.showMessage('Partners', showMessage);

          this.unmarkDirty();
          this.spinnerService.hide();
        },
        error: (err: HttpErrorResponse) => {
          this.unmarkDirty();
          this.load();
          this.dialogService.showMessage('Error', err.error);
        }
      });
  }

  onPage(ev: PageEvent) {
    this.pageIndex = ev.pageIndex;
    this.pageSize  = ev.pageSize;
  }

  goToPage(index: number): void {
    this.pageIndex = Math.min(Math.max(index, 0), this.totalPages - 1);
  }

  prevPage(): void {
    this.goToPage(this.pageIndex - 1);
  }

  nextPage(): void {
    this.goToPage(this.pageIndex + 1);
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    this.pageIndex = 0;
  }


  toggleSort(col: SortKey) {
    if (this.sortActive === col) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortActive = col;
      this.sortDir = 'asc';
    }
  }

  isSorted(col: SortKey, dir: 'asc' | 'desc') {
    return this.sortActive === col && this.sortDir === dir;
  }

  get filtered(): TtdPartner[] {
    const q = this.search;
    if (!q) return this.partners;
    return this.partners.filter(r => this.filterPredicate(r, q));
  }

  get sorted(): TtdPartner[] {
    const arr = [...this.filtered];
    const dir = this.sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let va: string | number = '';
      let vb: string | number = '';
      switch (this.sortActive) {
        case 'advertisers': va = this.advertisersCount(a); vb = this.advertisersCount(b); break;
        case 'secret': va = (a.firstPartyDataSecretKey ?? '').toLowerCase();
                       vb = (b.firstPartyDataSecretKey ?? '').toLowerCase();
                       break;
        default:
          va = (a as any)[this.sortActive]?.toString().toLowerCase() ?? '';
          vb = (b as any)[this.sortActive]?.toString().toLowerCase() ?? '';
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    });
    return arr;
  }

  get pageSlice(): TtdPartner[] {
    const start = this.pageIndex * this.pageSize;
    return this.sorted.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.sorted.length / this.pageSize));
  }

  get pageWindow(): (number | '…')[] {
    return this.buildPageWindow(this.pageIndex, this.totalPages);
  }

  private buildPageWindow(idx: number, total: number): (number|'…')[] {
    const current = idx + 1;
    const side = 1;
    const maxVisible = 7;

    if (total <= maxVisible) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number|'…')[] = [1];
    let start = Math.max(2, current - side);
    let end   = Math.min(total - 1, current + side);

    while ((end - start + 1) < (maxVisible - 2)) {
      if (start > 2) start--;
      else if (end < total - 1) end++;
      else break;
    }

    if (start > 2) pages.push('…');
    for (let p = start; p <= end; p++) pages.push(p);
    if (end < total - 1) pages.push('…');
    pages.push(total);
    return pages;
  }

  private escapeRegExp(s: string) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  private escapeHtml(s: string) { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

  highlight(value: string | number | null | undefined, query: string): string {
    if (value === null || value === undefined) return '';
    const text = String(value);
    const q = (query || '').trim();
    if (!q) return this.escapeHtml(text);
    const terms = q.split(/\s+/).filter(Boolean).map(t => this.escapeRegExp(t));
    const regex = new RegExp(`(${terms.join('|')})`, 'gi');
    const escaped = this.escapeHtml(text);
    return escaped.replace(regex, '<mark class="hl">$1</mark>');
  }

  private getUsedIds(): Set<string> {
    return new Set(this.partners.map(p => p.id));
  }
  private normalizeAds(ads?: TtdAdvertiser[]): TtdAdvertiser[] {
    return (ads ?? []).map(a => ({ id: a.id.trim(), name: a.name.trim() }));
  }

  private snapshot(): TtdPartner[] {
    return JSON.parse(JSON.stringify(this.partners));
  }

  private setBaseline(): void {
    this.baseline = this.snapshot();
    this.isDirty = false;
  }

  private markDirty(): void {
    this.isDirty = true;
  }

  private unmarkDirty(): void {
    this.isDirty = false;
  }
}
